import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FeatureCollection, validateBBox } from '@turf/helpers';
import { Feature, Geometry, Point } from 'geojson';
import { Tempo } from 'src/app/instant-analysis/models/tempo';
import { MathAux } from 'src/app/instant-analysis/services/math-aux/math-aux';
import { Renderers } from 'vega';
import embed, { EmbedOptions, Mode, VisualizationSpec } from 'vega-embed';
import { RainDuration } from '../models/rain-duration';
import { RainChartComponent } from '../rain-chart/rain-chart.component';
import { SearchFieldComponent } from '../search-field/search-field.component';
import { DiscreteChartService } from '../services/discrete-chart/discrete-chart.service';

/**
 * Elemento a ser exibido no gráfico de barras.
 */
interface RainDiscreteChartElement { 
  classificacao: string;
  estacao: string;
  duracao: number;
  porcentagem_tempo: number;
} 

/**
 * Gráfico de barras da duração das intensidades fraca, moderada, forte e muito forte
 * medidas pela cidade inteira e por cada estação separadamente.
 */
@Component({
  selector: 'app-discrete-chart',
  templateUrl: './discrete-chart.component.html',
  styleUrls: ['./discrete-chart.component.css']
})
export class DiscreteChartComponent implements OnInit {

  isDragDisabled: boolean = true;
  features: Feature<Geometry>[] = [];
  cityRainDurationInSeconds: RainDuration;
  toggleSelect = false;
  selectedChartType = "1"
  selectedStations: string[] = [];
  isExpansionPanelOpen: boolean = false;
  /**
  * Textos do tooltips do discretechartbar.
  */
  discreteChartTooltipText:string = `Duração das intensidades de chuva fraca,
    moderada, forte e muito forte. Exibe a média da cidade e é possível filtrar estações.
    Permite exibir as durações como valores absolutos ou porcentagens.`;
  
  @Output() dragDisabled = new EventEmitter<boolean>();
  @Output() close = new EventEmitter<boolean>();
  @Input() startDate: string;
  @Input() endDate: string;

  @ViewChild('searchField') searchField: SearchFieldComponent;

  constructor(private discreteChartService: DiscreteChartService) {
  }
  tempos: Tempo[] = []

  ngOnInit(): void {
  }

  /**
   * Atualiza o gráfico, exibindo as estações selecionadas no intervalo de datas selecionado pelo usuário.
   */
   async updateChart() {
    let spec : VisualizationSpec;
    if (this.features.length > 0){
    spec = this.createSpec();

    const embedOpts: EmbedOptions<string, Renderers> = {
      renderer: 'svg',
      actions: {
        export: true,
        source: false,
        compiled: false,
        editor: false
      },
      mode: 'vega-lite' as Mode,
      defaultStyle: true,
      i18n: {
        PNG_ACTION: "Salvar como PNG",
        SVG_ACTION: "Salvar como SVG",
      }
    };

    await embed('#plotting-area-2', spec, embedOpts);

  }
}

  /**
   * Cria a spec do gráfico de barras do vega-lite, diferenciando entre tempo de chuva em horas, ou
   * porcentagem de tempo por intensidade. 
   * @returns 
   */
  createSpec() {
    // const props = this.getDiscretequantidades();
    const props = this.getRainDuration();

    const { dynamicField, title, sort } = this.getChartEncodings();

    const spec : VisualizationSpec = {
      "width": 510,
      "data": {
        "values": props,
      },
      "mark": {"type": "bar", "tooltip": true},
      "encoding": {
        "facet": {
          "columns": 1,
          "field": "estacao",
          "title": "Estação",
          "type": "nominal",
          "sort": ["CIDADE DE NITERÓI"]
        },
        "y": {"field": "classificacao", "type": "nominal", "sort": sort, 
          "axis": {
          "title": null,
          "ticks": false,
          "labels": false
          }
        },
        "x": {"field": dynamicField, "type": "quantitative", "title": title, 
          "scale": (this.selectedChartType == "1") ? {} : {domain: [0, 100]}
        },
        "color": {"field":"classificacao","type":"nominal", "title": "Classificação", "scale": 
          {
            "domain": ["fraca", "moderada", "forte", "muito forte", "geral"],
            "range": ["#339944", "#FFCC00", "#FF8822", "#DD2233", "#555"]
          }
        }
      },
    };

    return spec;
  }

  /**
   * 
   */
  getRainDuration(): RainDiscreteChartElement[] {

    /**
     * 
     * @param intensityDurationInSeconds 
     * @param totalDurationInSeconds 
     * @returns 
     */
    const duration = (intensityDurationInSeconds: number, totalDurationInSeconds: number) => {
      let intensityDurationInHours = intensityDurationInSeconds / 3600;
      let intensityDurationInPercentage = MathAux.round(intensityDurationInHours / (totalDurationInSeconds / 3600) * 100, 4);

      return [ intensityDurationInHours, intensityDurationInPercentage ];
    };

    const rows: RainDiscreteChartElement[] = [];

    // Se for o gráfico de valores absolutos, adicionar as linhas da Cidade primeiro
    if (this.selectedChartType == "1") {
      let estacao = "CIDADE DE NITERÓI";
      let { nfraca, nmoderada, nforte, nmuitoforte, total } = this.cityRainDurationInSeconds;
      let [ fracaHours, fracaPercent ] = duration(nfraca, total);
      let [ moderadaHours, moderadaPercent ] = duration(nmoderada, total);
      let [ forteHours, fortePercent ] = duration(nforte, total);
      let [ muitoForteHours, muitoFortePercent ] = duration(nmuitoforte, total);
      let [ totalHours, totalPercent ] = duration(total, total);

      rows.push({ classificacao: "fraca", estacao, duracao: fracaHours, porcentagem_tempo: fracaPercent });
      rows.push({ classificacao: "moderada", estacao, duracao: moderadaHours, porcentagem_tempo: moderadaPercent });
      rows.push({ classificacao: "forte", estacao, duracao: forteHours, porcentagem_tempo: fortePercent });
      rows.push({ classificacao: "muito forte", estacao, duracao: muitoForteHours, porcentagem_tempo: muitoFortePercent });
      rows.push({ classificacao: "geral", estacao, duracao: totalHours, porcentagem_tempo: totalPercent });
    }

    // Apenas as estações filtradas
    let props = this.features
      .filter(station => {
        let { nome_estacao_original } = station.properties;
        return this.selectedStations.includes(nome_estacao_original);
      })
      .map(station => station.properties);

    props.forEach(station => {
      let { nome_estacao_original: estacao } = station;
      let [ fracaHours, fracaPercent ] = duration(station.nfraca, station.total);
      let [ moderadaHours, moderadaPercent ] = duration(station.nmoderada, station.total);
      let [ forteHours, fortePercent ] = duration(station.nforte, station.total);
      let [ muitoForteHours, muitoFortePercent ] = duration(station.nmuitoforte, station.total);
      let [ totalHours, totalPercent ] = duration(station.total, station.total);

      rows.push({ classificacao: "fraca", estacao, duracao: fracaHours, porcentagem_tempo: fracaPercent });
      rows.push({ classificacao: "moderada", estacao, duracao: moderadaHours, porcentagem_tempo: moderadaPercent });
      rows.push({ classificacao: "forte", estacao, duracao: forteHours, porcentagem_tempo: fortePercent });
      rows.push({ classificacao: "muito forte", estacao, duracao: muitoForteHours, porcentagem_tempo: muitoFortePercent });
      
      // Chuva absoluta inclui o tempo total. Porcentagem não precisa, porque vai ser 100% para cada estação.
      if (this.selectedChartType == "1") {
        rows.push({ classificacao: "geral", estacao, duracao: totalHours, porcentagem_tempo: totalPercent });
      }
    });

    return rows;
  }

  /**
   * Obtém os nomes dos atributos de encoding que variam dependendo dos dados serem sobre os tempos
   * absolutos, ou a porcentagem de tempo por intensidade de chuva.
   * @returns objetos contendo os nomes dos atributos de encoding do vega-lite relacionados ao 
   *    tipo de visualização do gráfico
   */
  getChartEncodings() {
    return this.selectedChartType == '1' ? 
      { 
        dynamicField: 'duracao'
        , title: 'Tempo (h)'
        , sort: ['fraca', 'moderada', 'forte', 'muito forte', 'geral']} : 
      { 
        dynamicField: 'porcentagem_tempo'
        , title: '% de tempo'
        , sort: ['fraca', 'moderada', 'forte', 'muito forte']
      };
  }

  /**
   * Solicita ao serviço do gráfico de barras os dados para a construção do gráfico.
   * @returns dados para a construção do gráfico de barras.
   */
  getDiscretequantidades() {
    return this.selectedChartType == '1' ? this.discreteChartService.calcAbsoluteRainTimePerIntensity()
      : this.discreteChartService.calcPercentageRainTimePerIntensity();
  }

  /**
   * Define o array de estações com o tempo e o valor do índice pluviométrico. O array é usado
   * na construção do gráfico de barras e na população do filtro de nomes das estações.
   * @param rainData 
   */
   setRainFeatures(features: Feature<Point>[]) {
    this.features = features;
    this.searchField.allOptions = Array.from(
        new Set(features.map(feature => feature.properties.nome_estacao_original)
      )
    );
  }

  setCityRainDurationInSeconds(cityRainDurationInSeconds: RainDuration) {
    this.cityRainDurationInSeconds = cityRainDurationInSeconds;
  }

  setTempos(tempos: Tempo[]){
    this.tempos = tempos
    this.startDate = this.tempoToDate(this.tempos[0])
    this.endDate = this.tempoToDate(this.tempos[1])
  }

  tempoToDate(tempo: Tempo): string{
    return `${tempo.dia.toString().padStart(2,"0")}-${tempo.mes.toString().padStart(2,"0")}-${tempo.ano.toString()}  ${tempo.hora.toString().padStart(2,"0")}:${tempo.minuto.toString().padStart(2,"0")}`
  } 

  headerMouseDown(event: any) {
    this.dragDisabled.emit(false)
  }

  /**
   * 
   * @param event 
   */
  headerMouseUp(event: any) {
    this.dragDisabled.emit(true);
  }

  closeChart() {
    this.closeExpansionPanel();
    this.close.emit();
  }

  /** 
   * Fecha o expansion panel do accordion. Isso é importante
   * para evitar que o expansion panel fique "por cima" do mapa
   * quando o componente do gráfico for fechado com visibility hidden.
   */
  closeExpansionPanel() {
    this.isExpansionPanelOpen = false;
  }

  /**
   * Disparado quando ocorre uma mudança no filtro das estações selecionadas. Atualiza a lista de
   * estações selecionadas no componente e no service, e solicita a atualização do gráfico para exibir
   * as estações selecionadas.
   * @param options 
   */
  onSelectedOptionsChanged(options: string[]) {
    this.selectedStations = options;
    this.discreteChartService.selectedOptions = options;
    this.updateChart();
  }


}
